import React, { useState, useRef, useEffect } from 'react';
import owlVideo from '../Videos/Owl.mp4';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { doc, setDoc, getDoc, collection } from 'firebase/firestore';
import { auth, firestore } from '../firebase';
import logo from './Logo_White.jpg';
import Spinner from './Spinner';
// Replace shadcn import with react-otp-input
import OtpInput from 'react-otp-input';
import RollingTextButton from './RollingTextButton';
// Import the WavyBackground component
import './PhoneAuth.css';

function PhoneAuth() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValidated, setOtpValidated] = useState(false);
  const [isKannada, setIsKannada] = useState(false);
  const videoRef = useRef(null);

  // iOS video handling for language switching
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Force video to play after language switch on iOS
      const playVideo = async () => {
        try {
          // Reset video to beginning
          video.currentTime = 0;
          // Ensure video properties are set
          video.muted = true;
          video.playsInline = true;
          video.autoplay = true;
          // Attempt to play
          await video.play();
        } catch (error) {
          console.log('Video autoplay failed after language switch:', error);
          // Fallback: try again after a short delay
          setTimeout(async () => {
            try {
              await video.play();
            } catch (retryError) {
              console.log('Video retry failed:', retryError);
            }
          }, 100);
        }
      };
      
      // Play video when language changes
      playVideo();
    }
  }, [isKannada]);

  const setCookie = (name, value, days) => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + days);
    const cookieValue = encodeURIComponent(value) + 
      ((days) ? `; expires=${expirationDate.toUTCString()}` : '') + 
      '; path=/; SameSite=Lax';
    document.cookie = `${name}=${cookieValue}`;
    console.log(`Cookie set: ${name}=${value} (expires in ${days} days)`);
  };

  const getCookie = (name) => {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        const value = decodeURIComponent(cookie.substring(nameEQ.length));
        console.log(`Cookie retrieved: ${name}=${value}`);
        return value;
      }
    }
    return null;
  };

  // Enhanced MAC address capture for law enforcement compliance
  const getMacAddress = () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Log all URL parameters for debugging
    const allParams = Object.fromEntries(urlParams.entries());
    console.log('All URL params:', allParams);
    
    // Extended MAC address parameter names for different Mikrotik configurations
    const macParams = [
      'client_mac', 'mac', 'mac-address', 'macaddr', 'address', 'clientmac', 'client-mac',
      'client-mac-address', 'device-mac', 'device_mac', 'hw-address', 'hw_address',
      'ethernet-address', 'ethernet_address', 'link-address', 'link_address',
      'client-hw-addr', 'client_hw_addr', 'station-mac', 'station_mac'
    ];
    
    let mac = null;
    
    // Check for MAC address in URL parameters with validation
    const possibleParams = ['client_mac', 'mac-address', 'mac', 'MAC', 'macaddr', 'macaddress', 'mac_address', 'device_mac', 'device_id'];
    
    for (const param of possibleParams) {
      if (urlParams.get(param)) {
        mac = urlParams.get(param);
        console.log(`Found MAC in parameter '${param}':`, mac);
        break;
      }
    }
    
    // If not found in standard parameters, check if it's in any parameter
    if (!mac) {
      for (const [key, value] of urlParams.entries()) {
        // Skip dst and other unwanted parameters
        if (key === 'dst' || key === 'link-login' || key === 'link-orig') {
          continue;
        }
        
        // More flexible regex to catch various MAC formats
        if (value && (
          value.match(/([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})/i) || // XX:XX:XX:XX:XX:XX format
          value.match(/([0-9A-Fa-f]{12})/i) ||                       // XXXXXXXXXXXX format
          value.match(/([0-9A-Fa-f]{4}[.-]){2}([0-9A-Fa-f]{4})/i)    // XXXX.XXXX.XXXX format
        )) {
          mac = value;
          console.log(`Found MAC-like value in parameter '${key}':`, mac);
          break;
        }
      }
    }
    
    // If MAC address is found, clean and format it
    if (mac) {
      // Clean and validate the MAC address format
      let cleanMac = mac.replace(/[^A-Fa-f0-9]/g, '').toUpperCase();
      console.log('Cleaned MAC:', cleanMac);
      
      // Format MAC address with colons (XX:XX:XX:XX:XX:XX) in uppercase
      try {
        if (cleanMac.length === 12) {
          const formattedMac = cleanMac.match(/.{1,2}/g).join(':').toUpperCase();
          console.log('Formatted MAC:', formattedMac);
          
          // Store in both localStorage and cookies for future use
          localStorage.setItem('deviceIdentifier', formattedMac);
          setCookie('deviceIdentifier', formattedMac, 365); // Store for 1 year
          
          return formattedMac;
        } else {
          console.warn(`MAC address has unusual length (${cleanMac.length}): ${mac}`);
          // Store original MAC in localStorage and cookies anyway but convert to uppercase
          const uppercaseMac = mac.toUpperCase();
          localStorage.setItem('deviceIdentifier', uppercaseMac);
          setCookie('deviceIdentifier', uppercaseMac, 365);
          return uppercaseMac; // Return uppercase original if length is not 12
        }
      } catch (error) {
        console.error('Error formatting MAC address:', error);
        // Store original MAC in localStorage and cookies anyway but convert to uppercase
        const uppercaseMac = mac.toUpperCase();
        localStorage.setItem('deviceIdentifier', uppercaseMac);
        setCookie('deviceIdentifier', uppercaseMac, 365);
        return uppercaseMac; // Return uppercase original if formatting fails
      }
    }
    
    // If no MAC address is found, try alternative identifiers
    
    // Try to get device identifier from localStorage or cookies if it was previously stored
    const storedId = localStorage.getItem('deviceIdentifier') || getCookie('deviceIdentifier');
    if (storedId) {
      console.log('Using stored device identifier:', storedId);
      // Ensure it's stored in both places
      localStorage.setItem('deviceIdentifier', storedId);
      setCookie('deviceIdentifier', storedId, 365);
      return storedId;
    }
    
    console.warn('No MAC address found in URL parameters, localStorage, or cookies');
    
    // Rest of the function remains the same...
    
    // Try to get other device identifiers from URL parameters
    const deviceId = urlParams.get('device_id') || urlParams.get('deviceid') || urlParams.get('id');
    if (deviceId) {
      console.log('Using device ID from URL:', deviceId);
      const formattedId = `DEVICE-ID-${deviceId}`;
      localStorage.setItem('deviceIdentifier', formattedId);
      return formattedId;
    }
    
    // Try to get hostname or device name
    const hostname = urlParams.get('hostname') || urlParams.get('host_name') || urlParams.get('device_name');
    if (hostname) {
      console.log('Using hostname from URL:', hostname);
      const formattedHostname = `HOST-${hostname}`;
      localStorage.setItem('deviceIdentifier', formattedHostname);
      return formattedHostname;
    }
    
    // For iOS devices, try to use IP address as a fallback identifier
    const ip = urlParams.get('ip');
    if (ip) {
      console.log('Using IP address as device identifier:', ip);
      const ipBasedId = `IP-${ip.replace(/\./g, '-')}`;
      localStorage.setItem('deviceIdentifier', ipBasedId);
      return ipBasedId;
    }
    
    // As a last resort, generate a unique device fingerprint that includes more entropy
    const fingerprint = generateUniqueDeviceFingerprint();
    console.log('Generated unique device fingerprint:', fingerprint);
    localStorage.setItem('deviceIdentifier', fingerprint);
    return fingerprint;
  };
  
  // Improved fingerprint generation with more entropy and randomness
  const generateUniqueDeviceFingerprint = () => {
    const components = [];
    
    // Add user agent
    components.push(navigator.userAgent);
    
    // Add screen dimensions
    components.push(`${window.screen.width}x${window.screen.height}`);
    
    // Add color depth
    components.push(window.screen.colorDepth);
    
    // Add timezone
    components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
    
    // Add language
    components.push(navigator.language);
    
    // Add platform
    components.push(navigator.platform);
    
    // Add device memory if available
    if (navigator.deviceMemory) components.push(navigator.deviceMemory);
    
    // Add hardware concurrency if available
    if (navigator.hardwareConcurrency) components.push(navigator.hardwareConcurrency);
    
    // Add random component for uniqueness
    components.push(Math.random().toString(36).substring(2, 10));
    
    // Add current timestamp for uniqueness
    components.push(Date.now().toString());
    
    // Join all components and create a hash-like string
    const fingerprint = btoa(components.join('|')).replace(/[^a-zA-Z0-9]/g, '').substring(0, 12);
    
    // Create a more descriptive device ID
    const deviceType = /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(navigator.userAgent) ? 'Mobile' : 'Desktop';
    const browser = navigator.userAgent.match(/chrome|firefox|safari|opera|edge|ie/i)?.[0] || 'unknown';
    
    return `${deviceType}-${browser}-${fingerprint.substring(0, 6)}`;
  };

  // Enhanced device tracking for law enforcement compliance
  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    const urlParams = new URLSearchParams(window.location.search);
    
    return {
      // Device identification
      macAddress: getMacAddress(),
      deviceFingerprint: urlParams.get('device_fingerprint') || generateUniqueDeviceFingerprint(),
      
      // Network information from Mikrotik
      clientIP: urlParams.get('ip') || urlParams.get('client-ip') || urlParams.get('client_ip') || 'unknown',
      hostname: urlParams.get('hostname') || urlParams.get('host-name') || 'unknown',
      interface: urlParams.get('interface') || urlParams.get('if') || 'unknown',
      sessionId: urlParams.get('sessionid') || urlParams.get('session-id') || 'unknown',
      
      // Device characteristics
      deviceType: /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(userAgent) ? 'Mobile' : 'Desktop',
      browser: navigator.userAgent.match(/chrome|firefox|safari|opera|edge|ie/i)?.[0] || 'unknown',
      os: navigator.userAgent.match(/windows|mac|linux|android|ios/i)?.[0] || 'unknown',
      userAgent: userAgent,
      
      // Additional tracking data
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      
      // Timestamps
      accessTime: new Date().toISOString(),
      accessTimeLocal: formatDateTime(new Date())
    };
  };

  const setupRecaptcha = () => {
    try {
      // Clear any existing reCAPTCHA instances
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
      
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
          window.recaptchaVerifier = null;
        },
        'error-callback': (error) => {
          console.error('reCAPTCHA error:', error);
          window.recaptchaVerifier = null;
        }
      });

      return window.recaptchaVerifier.render();
    } catch (error) {
      console.error('reCAPTCHA setup error:', error);
      setLoading(false);
      setOtpSent(false);
      throw error; // Propagate error to sendOTP
    }
  };

  const sendOTP = async (e) => {
    e.preventDefault();
    if (!acceptTerms) {
      alert('Please accept the terms and conditions');
      return;
    }

    if (phoneNumber.length !== 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    const formattedPhoneNumber = `+91${phoneNumber.trim()}`;

    try {
      setLoading(true);
      const blacklistRef = doc(collection(firestore, 'blacklist'), formattedPhoneNumber);
      const blacklistDoc = await getDoc(blacklistRef);

      if (blacklistDoc.exists()) {
        alert('User is blocked. Further attempts would be reported to Law Enforcement Agencies.');
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Error checking blacklist:', error);
      // If permission denied, log but continue with OTP flow
      if (error.code === 'permission-denied') {
        console.warn('Blacklist check skipped due to permissions - continuing with OTP');
      } else {
        alert('An error occurred. Please try again later.');
        setLoading(false);
        return;
      }
    }

        try {
      setLoading(true);
      await setupRecaptcha();
      
      if (!window.recaptchaVerifier) {
        throw new Error('reCAPTCHA not initialized');
      }

      console.log('Sending OTP to:', formattedPhoneNumber);
      
      const confirmation = await signInWithPhoneNumber(auth, formattedPhoneNumber, window.recaptchaVerifier);
      
      if (confirmation) {
        setConfirmationResult(confirmation);
        setOtpSent(true);
        startCountdown();
        alert('OTP sent successfully!');
      } else {
        throw new Error('Failed to send OTP');
      }
    } catch (error) {
      console.error('OTP Send Error:', error);
      
      // Handle specific Firebase errors
      if (error.code === 'auth/too-many-requests') {
        alert('Too many OTP requests. Please try again after some time or contact support if the issue persists.');
      } else if (error.code === 'auth/invalid-phone-number') {
        alert('Invalid phone number format. Please check and try again.');
      } else {
        alert('Failed to send OTP. Please try again later.');
      }

      // Reset states
      setOtpSent(false);
      setCountdown(60);
      
      // Clear reCAPTCHA
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setLoading(false);
    }
  };
  const formatDateTime = (date) => {
    const pad = (num) => num.toString().padStart(2, '0');
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };
  
  const verifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (!confirmationResult) {
        console.error('Confirmation result is null. OTP might not have been sent.');
        alert('Please request OTP first');
        setLoading(false);
        return;
      }
  
      const cleanOTP = otp.replace(/\D/g, '');
      if (cleanOTP.length !== 6) {
        alert('Please enter exactly 6 digits');
        setLoading(false);
        return;
      }
  
      console.log('Attempting to verify OTP:', cleanOTP);
      const result = await confirmationResult.confirm(cleanOTP);
      
      if (!result?.user) {
        throw new Error('Verification failed - No user returned');
      }
  
      const user = result.user;
      console.log('User verified successfully:', user.uid);
      
      // Get device information with MAC address
      const deviceInfo = getDeviceInfo();
      const macAddress = deviceInfo.macAddress;
      console.log('Captured device identifier:', macAddress);
      
      // Capture additional MikroTik parameters, but filter out unwanted ones
      const urlParams = new URLSearchParams(window.location.search);
      const mikrotikParams = {
        ip: urlParams.get('ip') || 'unknown',
        hostname: urlParams.get('hostname') || 'unknown',
        interface: urlParams.get('interface') || 'unknown'
        // Deliberately not including 'dst' and other unwanted parameters
      };
      
      const now = new Date();
      const timestamp = now.getTime();
      const formattedDate = formatDateTime(now);
  
      // Create a unique document ID that includes the timestamp to prevent overwriting
      const docId = `${phoneNumber}_${timestamp}`;
      
      // Store login data with device identifier prominently
      const loginData = {
        uid: user.uid,
        phoneNumber: `+91${phoneNumber}`,
        timestamp: timestamp, // Store raw timestamp for sorting
        lastLogin: formattedDate, // Store formatted date in dd/mm/yyyy format
        formattedDate: formattedDate.split(',')[0], // Store just the date part in dd/mm/yyyy format
        // Store the MAC/device identifier at the top level for easier access
        macAddress: macAddress,
        // Also include it in deviceInfo for consistency
        deviceInfo: {
          ...deviceInfo,
          // Add MikroTik specific information
          mikrotik: mikrotikParams
        }
      };
      
      // Log the complete data being stored
      console.log('Storing login data:', JSON.stringify(loginData));
      
      // Store the data in Firestore
      await setDoc(doc(firestore, 'user_logins', docId), loginData);
  
      console.log('New login entry stored with identifier:', macAddress);
      console.log('New login entry stored for phone number:', phoneNumber);
      
      setOtpValidated(true);
      
      const redirectUrl = process.env.REACT_APP_CAPTIVE_PORTAL_URL;
      if (redirectUrl) {
        window.location.replace(redirectUrl);
      } else {
        console.error("CAPTIVE_PORTAL_URL is undefined");
      }
    } catch (error) {
      console.error('OTP Verification Error:', error);
      // Handle specific Firebase errors
      if (error.code === 'auth/invalid-verification-code') {
        alert('Invalid OTP. Please check the code and try again.');
      } else if (error.code === 'auth/code-expired') {
        alert('OTP has expired. Please request a new one.');
      } else {
        alert(`Verification failed: ${error.message}`);
      }
      setOtpValidated(false);
    } finally {
      setLoading(false);
    }
  };

  const startCountdown = () => {
    const intervalId = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(intervalId);
          setLoading(false);
          setOtpSent(false);
          return 60;
        }
        return prevCountdown - 1;
      });
    }, 1000);
  };
  const handlePhoneNumberChange = (e) => setPhoneNumber(e.target.value);
  
  // Remove the handleOtpChange function since OtpInput directly updates the state
  // const handleOtpChange = (e) => {
  //   // Allow only numeric input
  //   const value = e.target.value.replace(/\D/g, '');
  //   setOtp(value.slice(0, 6)); // Limit to 6 digits
  // };
  
    return (
    <div className="phone-auth-container">
      <div className="auth-content-wrapper">
        <div className="logo-title-container">
          <div className="logo-container shimmer-effect">
            <img src={logo} className="logo" alt="Brewz Coffee Logo" />
          </div>
          <h2 className={isKannada ? 'kannada-text' : ''}>
            {isKannada ? (
              <>ಬ್ರೂಜ಼್ ಕಾಫಿ<br />ಉಚಿತ ವೈ-ಫೈ</>
            ) : (
              <>Brewz Coffee<br />Free WiFi</>
            )}
          </h2>
        </div>
        <div className="terms-and-conditions terms-neumorphic">
          <button 
            className={`language-toggle ${isKannada ? 'kannada-mode' : ''}`}
            onClick={() => setIsKannada(!isKannada)}
          >
            {isKannada ? 'En' : 'ಕ'}
          </button>
          
          {!isKannada ? (
            <>
              <h3>Free WiFi – Terms</h3>
              <div className="terms-block">
                <p>By using our free WiFi, you agree to:</p>
                <ul>
                  <li><strong>Personal Use Only</strong> – No commercial or illegal activity (hacking, spamming, piracy, etc.).</li>
                  <li><strong>Security Risks</strong> – Public WiFi may be unsafe; Brewz Coffee isn't liable for data loss or breaches.</li>
                  <li><strong>Fair Use</strong> – Avoid heavy downloads/streaming that affect others.</li>
                  <li><strong>Termination</strong> – Access may be revoked for violations or disruptive behavior.</li>
                </ul>
              </div>
              <p>#GetBrewzd</p>
            </>
          ) : (
            <div className="kannada-text">
              <h3>ಉಚಿತ ವೈ-ಫೈ – ನಿಯಮಗಳು</h3>
              <div className="terms-block">
                <p>ನಮ್ಮ ಉಚಿತ ವೈ-ಫೈ ಬಳಸುವುದರಿಂದ, ನೀವು ಒಪ್ಪುವುದೇನೆಂದರೆ:</p>
                <ul>
                  <li><strong>ವೈಯಕ್ತಿಕ ಬಳಕೆ ಮಾತ್ರ</strong> – ವಾಣಿಜ್ಯ ಅಥವಾ ಅನಧಿಕೃತ ಚಟುವಟಿಕೆ (ಹ್ಯಾಕಿಂಗ್, ಸ್ಪ್ಯಾಮ್, ಪೈರಸಿ ಇತ್ಯಾದಿ) ನಿಷಿದ್ಧ.</li>
                  <li><strong>ಭದ್ರತಾ ಅಪಾಯಗಳು</strong> – ಸಾರ್ವಜನಿಕ ವೈ-ಫೈ ಸುರಕ್ಷಿತವಿರದೇ ಇರಬಹುದು; ಡೇಟಾ ನಷ್ಟ ಅಥವಾ ಹ್ಯಾಕಿಂಗ್‌ಗೆ Brewz Coffee ಹೊಣೆಗಾರರಲ್ಲ.</li>
                  <li><strong>ನ್ಯಾಯಸಮ್ಮತ ಬಳಕೆ</strong> – ಇತರರ ಸೇವೆಯನ್ನು ಪ್ರಭಾವಿಸುವ ಹೆಚ್ಚಿನ ಡೌನ್‌ಲೋಡ್‌ಗಳು/ಸ್ಟ್ರೀಮಿಂಗ್‌ ತಪ್ಪಿಸಿ.</li>
                  <li><strong>ರದ್ದುಪಡಿಸುವ ಹಕ್ಕು</strong> – ನಿಯಮ ಉಲ್ಲಂಘನೆ ಅಥವಾ ಅಶಿಸ್ತಿನ ವರ್ತನೆಯಿಂದ ಪ್ರವೇಶವನ್ನು ನಿಲ್ಲಿಸಲಾಗಬಹುದು.</li>
                </ul>
              </div>
              <p>#GetBrewzd</p>
            </div>
          )}
          
          <video 
            ref={videoRef}
            className="video-placeholder-overlay" 
            src={owlVideo} 
            autoPlay 
            loop 
            muted 
            playsInline
            controls={false}
            disablePictureInPicture
            preload="auto"
            webkit-playsinline="true"
            onLoadedData={() => {
              // Ensure video plays when loaded, especially important for iOS
              if (videoRef.current) {
                videoRef.current.play().catch(error => {
                  console.log('Initial video play failed:', error);
                });
              }
            }}
          />
        </div>
        <div className="accept-terms">
          <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
          <span className={isKannada ? 'kannada-text' : ''}>
            {isKannada ? 'ನಾನು ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ಒಪ್ಪಿಕೊಳ್ಳುತ್ತೇನೆ' : 'I Accept the Terms and Conditions'}
          </span>
        </div>
        <form onSubmit={!confirmationResult ? sendOTP : verifyOTP}>
          {!confirmationResult ? (
            <>
                <div className="phone-input-container">
                  <span className="country-code">+91</span>
                  <input
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    placeholder={isKannada ? "ಫೋನ್ ಸಂಖ್ಯೆ ನಮೂದಿಸಿ" : "Enter Phone Number"}
                    required
                    className="neumorphic-input"
                    maxLength="12"
                    onInvalid={(e) => e.target.setCustomValidity('Input Required')}
                    onInput={(e) => e.target.setCustomValidity('')}
                  />
                </div>
                {!loading && !otpSent && (
                  <button type="submit" className={`accent accent-blue ${isKannada ? 'kannada-text' : ''}`}>
                    {isKannada ? 'OTP ಕಳುಹಿಸಿ' : 'Send OTP'}
                  </button>
                )}
              <div id="recaptcha-container"></div>
              {otpSent ? (
                <div className={`countdown-message ${isKannada ? 'kannada-text' : ''}`}>
                  {isKannada ? `OTP ಕಳುಹಿಸಲಾಗಿದೆ! ${countdown}ಸೆ ನಲ್ಲಿ ಮರು ಕಳುಹಿಸಿ` : `OTP sent! Resend in ${countdown}s`}
                </div>
              ) : loading ? (
                <div className="loading-container">
                  <Spinner />
                  <div style={{ color: 'white', fontWeight: 'bold' }} className={isKannada ? 'kannada-text' : ''}>
                    {isKannada ? 'OTP ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ...' : 'Sending OTP...'}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="otp-input-container">
                <label className={isKannada ? 'kannada-text' : ''}>
                  {isKannada ? 'OTP ನಮೂದಿಸಿ' : 'ENTER OTP'}
                </label>
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  inputType="tel"
                  shouldAutoFocus
                  renderSeparator={<span></span>}
                  renderInput={(props) => <input {...props} />}
                  inputStyle="neumorphic-input otp-digit"
                  containerStyle="otp-container"
                />
                <button 
                  type="submit" 
                  disabled={otpValidated || loading} 
                  className={`accent accent-blue ${isKannada ? 'kannada-text' : ''}`}
                  style={{ display: loading ? 'none' : 'flex' }}
                >
                  {loading ? (
                    <>
                      <span className={isKannada ? 'kannada-text' : ''}>
                        {isKannada ? 'ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ...' : 'Validating...'}
                      </span>
                      <Spinner style={{ width: '20px', height: '20px', marginLeft: '10px' }} />
                    </>
                  ) : (
                    isKannada ? 'OTP ಪರಿಶೀಲಿಸಿ' : 'Validate OTP'
                  )}
                </button>
                {loading && (
                  <div className="loading-container" style={{ marginTop: '15px' }}>
                    <Spinner />
                    <div style={{ color: 'white', fontWeight: 'bold' }} className={isKannada ? 'kannada-text' : ''}>
                      {isKannada ? 'OTP ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ...' : 'Validating OTP...'}
                    </div>
                  </div>
                )}
                {!loading && otpValidated && (
                  <div style={{ color: 'green', fontWeight: 'bold', marginTop: '10px' }} className={isKannada ? 'kannada-text' : ''}>
                    {isKannada ? 'ಪರಿಶೀಲನೆ ಯಶಸ್ವಿ! ಮರುನಿರ್ದೇಶನ...' : 'Validation Successful! Redirecting...'}
                  </div>
                )}
              </div>
            </>
          )}
        </form>
        <div className="rolling-button-container">
          <RollingTextButton />
        </div>
      </div>
    </div>
);

}

// Add this export statement at the end of the file
export default PhoneAuth;